import { Controller } from '@hotwired/stimulus'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import {
  ROOT_GREEN, ROOT_BLUE, ROOT_BURGUNDY, ROOT_HONEY,
} from '@/constants/colors'

gsap.registerPlugin(ScrollTrigger)

const COLOR_MAP = {
  green: ROOT_GREEN,
  blue: ROOT_BLUE,
  burgundy: ROOT_BURGUNDY,
  honey: ROOT_HONEY,
}

function connectorColor(connector) {
  for (const [key, color] of Object.entries(COLOR_MAP)) {
    if (connector.classList.contains(`timeline-connector-${key}`)) return color
  }
  return ROOT_GREEN
}

function createSVGElement(tag, attrs = {}) {
  const el = document.createElementNS('http://www.w3.org/2000/svg', tag)
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v)
  return el
}

function createGradientStop(offset, color, opacity = null) {
  const attrs = { offset, 'stop-color': color }
  if (opacity !== null) attrs['stop-opacity'] = String(opacity)
  return createSVGElement('stop', attrs)
}

function setupDrawAnimation(path, stroke, strokeWidth) {
  path.setAttribute('fill', 'none')
  path.setAttribute('stroke', stroke)
  path.setAttribute('stroke-width', String(strokeWidth))
  path.setAttribute('pathLength', '1')
  path.style.strokeDasharray = '1'
  path.style.strokeDashoffset = '1'
  path.style.transition = 'none'
}

function buildBorderPerimeter(isRight, dims, meetY) {
  const {
    strokeOffset: s, cornerRadius: r, width: w, height: h,
  } = dims
  if (isRight) {
    return [
      `L ${s},${h - s - r}`,
      `A ${r},${r} 0 0 0 ${s + r},${h - s}`,
      `L ${w - s - r},${h - s}`,
      `A ${r},${r} 0 0 0 ${w - s},${h - s - r}`,
      `L ${w - s},${s + r}`,
      `A ${r},${r} 0 0 0 ${w - s - r},${s}`,
      `L ${s + r},${s}`,
      `A ${r},${r} 0 0 0 ${s},${s + r}`,
      `L ${s},${meetY}`,
    ]
  }
  return [
    `L ${w - s},${h - s - r}`,
    `A ${r},${r} 0 0 1 ${w - s - r},${h - s}`,
    `L ${s + r},${h - s}`,
    `A ${r},${r} 0 0 1 ${s},${h - s - r}`,
    `L ${s},${s + r}`,
    `A ${r},${r} 0 0 1 ${s + r},${s}`,
    `L ${w - s - r},${s}`,
    `A ${r},${r} 0 0 1 ${w - s},${s + r}`,
    `L ${w - s},${meetY}`,
  ]
}

function buildMobilePerimeter(isRight, dims, meetX) {
  const {
    strokeOffset: s, cornerRadius: r, width: w, height: h,
  } = dims
  if (isRight) {
    return [
      `L ${w - s - r},${s}`,
      `A ${r},${r} 0 0 1 ${w - s},${s + r}`,
      `L ${w - s},${h - s - r}`,
      `A ${r},${r} 0 0 1 ${w - s - r},${h - s}`,
      `L ${s + r},${h - s}`,
      `A ${r},${r} 0 0 1 ${s},${h - s - r}`,
      `L ${s},${s + r}`,
      `A ${r},${r} 0 0 1 ${s + r},${s}`,
      `L ${meetX},${s}`,
    ]
  }
  return [
    `L ${s + r},${s}`,
    `A ${r},${r} 0 0 0 ${s},${s + r}`,
    `L ${s},${h - s - r}`,
    `A ${r},${r} 0 0 0 ${s + r},${h - s}`,
    `L ${w - s - r},${h - s}`,
    `A ${r},${r} 0 0 0 ${w - s},${h - s - r}`,
    `L ${w - s},${s + r}`,
    `A ${r},${r} 0 0 0 ${w - s - r},${s}`,
    `L ${meetX},${s}`,
  ]
}

function killTimeline(tl) {
  if (!tl) return
  if (tl.scrollTrigger) tl.scrollTrigger.kill()
  tl.kill()
}

export default class extends Controller {
  static targets = ['line', 'connector', 'card', 'cardContent']

  static values = {
    scrub: { type: Boolean, default: true },
    start: { type: String, default: 'top top' },
  }

  static DESKTOP_TIMING = {
    connectorDraw: 0.04,
    borderDraw: 0.05,
    contentReveal: 0.04,
    pause: 0.06,
  }

  static MOBILE_TIMING = {
    borderDrawFull: 0.45,
    bgReveal: 0.15,
    contentReveal: 0.2,
  }

  connect() {
    // ScrollTrigger's pin reparents the element, which triggers disconnect/connect.
    if (this._pinActive) return

    this.timeline = null
    this.preTimeline = null
    this.mobileTimelines = []
    this.borderSVGs = []
    this.mediaQuery = window.matchMedia('(min-width: 992px)')
    this.uid = `tg-${Math.random().toString(36).slice(2, 8)}`
    this._disconnected = false
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (this.prefersReducedMotion) return

    // Wait for fonts so card measurements reflect final text layout.
    this._fontsReady = (document.fonts?.ready || Promise.resolve()).then(() => {
      if (this._disconnected || !document.contains(this.element)) return

      if (this.mediaQuery.matches) {
        this.setupDesktopAnimation()
      } else {
        this.setupMobileAnimation()
      }
    })

    this.handleMediaChange = this.handleMediaChange.bind(this)
    this.mediaQuery.addEventListener('change', this.handleMediaChange)

    this._resizeTimer = null
    this.handleResize = () => {
      clearTimeout(this._resizeTimer)
      this._resizeTimer = setTimeout(() => {
        this._fontsReady.then(() => {
          if (this._disconnected) return
          this.cleanup()
          if (this.mediaQuery.matches) {
            this.setupDesktopAnimation()
          } else {
            this.setupMobileAnimation()
          }
        })
      }, 250)
    }
    window.addEventListener('resize', this.handleResize)
  }

  disconnect() {
    // Only clean up if the element was truly removed (not just pin reparenting).
    if (document.contains(this.element)) return

    this._disconnected = true
    this._pinActive = false
    this.cleanup()
    this.mediaQuery.removeEventListener('change', this.handleMediaChange)
    window.removeEventListener('resize', this.handleResize)
    clearTimeout(this._resizeTimer)
  }

  handleMediaChange(e) {
    clearTimeout(this._resizeTimer)
    this._fontsReady.then(() => {
      if (this._disconnected) return
      this.cleanup()
      if (e.matches) {
        this.setupDesktopAnimation()
      } else {
        this.setupMobileAnimation()
      }
    })
  }

  // Desktop animation — pinned scroll-driven with vertical line

  setupDesktopAnimation() {
    this.cleanup()
    this._pinActive = true
    this.isDesktop = true

    this.naturalHeight = this.element.scrollHeight
    this.cachedPositions = this.getConnectorPositions()

    this.activateConnectors()
    this.createBorderSVGs()

    // Wrap timeline children with reduced top padding. The static layout uses
    // a large top padding (200px) that creates dead scroll space.
    this.innerWrap = document.createElement('div')
    const timelineStyle = getComputedStyle(this.element)
    const origPadTop = parseFloat(timelineStyle.paddingTop) || 0
    const animPadTop = Math.min(origPadTop, 40)
    const padReduction = origPadTop - animPadTop
    this.innerWrap.style.cssText = [
      'position: relative',
      'will-change: transform',
      `padding-top: ${animPadTop}px`,
      `padding-bottom: ${timelineStyle.paddingBottom}`,
    ].join('; ')

    const origHeight = this.naturalHeight
    this.naturalHeight -= padReduction
    this.cachedPositions = this.cachedPositions.map((pos) => {
      const pixelY = pos * origHeight - padReduction
      return Math.max(0, pixelY / this.naturalHeight)
    })
    while (this.element.firstChild) {
      this.innerWrap.appendChild(this.element.firstChild)
    }
    this.element.appendChild(this.innerWrap)
    this.element.style.paddingTop = '0'
    this.element.style.paddingBottom = '0'

    if (this.hasLineTarget) {
      const gradient = this.buildLineGradient(this.cachedPositions)
      if (gradient) this.lineTarget.style.background = gradient
    }

    // Pin the parent section so the heading stays visible.
    this.sectionEl = this.element.closest('section') || this.element.parentElement
    const timelineRect = this.element.getBoundingClientRect()
    const sectionRect = this.sectionEl.getBoundingClientRect()
    this.topOffset = timelineRect.top - sectionRect.top
    const bottomPad = parseFloat(getComputedStyle(this.sectionEl).paddingBottom) || 0
    this.viewportH = window.innerHeight - this.topOffset - bottomPad

    this.element.style.height = `${this.viewportH}px`
    this.element.style.overflow = 'hidden'

    this.setInitialStates()
    this.buildDesktopTimeline()
  }

  getConnectorPositions() {
    const timelineHeight = this.element.scrollHeight
    return this.connectorTargets.map((connector) => {
      const item = connector.closest('.timeline-item')
      const visualTop = item.offsetTop + (connector.offsetTop - connector.offsetHeight / 2)
      return visualTop / timelineHeight
    })
  }

  activateConnectors() {
    this.connectorTargets.forEach((c) => c.classList.add('tree-growing-active'))
  }

  buildLineGradient(positions) {
    const colors = this.connectorTargets.map(connectorColor)
    if (colors.length === 0) return null

    const pct = (v) => `${(Math.max(0, Math.min(1, v)) * 100).toFixed(1)}%`
    const stops = []

    stops.push(`transparent ${pct(positions[0] - 0.05)}`)
    stops.push(`${colors[0]} ${pct(positions[0] - 0.01)}`)

    for (let i = 0; i < positions.length; i++) {
      stops.push(`${colors[i]} ${pct(positions[i])}`)
      if (i < positions.length - 1) {
        const mid = (positions[i] + positions[i + 1]) / 2
        const blend = 0.03
        stops.push(`${colors[i]} ${pct(mid - blend)}`)
        stops.push(`${colors[i + 1]} ${pct(mid + blend)}`)
      }
    }

    const last = positions.length - 1
    stops.push(`${colors[last]} ${pct(positions[last])}`)
    stops.push(`transparent ${pct(positions[last] + 0.005)}`)

    return `linear-gradient(to bottom, ${stops.join(', ')})`
  }

  createBorderSVGs() {
    this.borderSVGs = []

    this.cardTargets.forEach((card, index) => {
      const connector = this.connectorTargets[index]
      const isRight = !!connector?.closest('.timeline-item-right')
      const cardStyle = getComputedStyle(card)
      const borderColor = cardStyle.borderTopColor
      const borderWidth = parseFloat(cardStyle.borderTopWidth) || 10
      const borderRadius = parseFloat(cardStyle.borderTopLeftRadius) || 16
      const width = card.offsetWidth
      const height = card.offsetHeight
      const strokeOffset = borderWidth / 2
      const cornerRadius = Math.max(0, borderRadius - strokeOffset)

      const svg = createSVGElement('svg', {
        viewBox: `0 0 ${width} ${height}`,
        overflow: 'visible',
      })
      svg.style.cssText = [
        'position: absolute',
        `top: ${-borderWidth}px`,
        `left: ${-borderWidth}px`,
        `width: ${width}px`,
        `height: ${height}px`,
        'max-width: none',
        'max-height: none',
        'pointer-events: none',
        'z-index: 3',
        'overflow: visible',
      ].join('; ')

      const borderPath = createSVGElement('path')
      let d
      let stroke = borderColor

      const dims = {
        width, height, strokeOffset, cornerRadius,
      }

      if (this.isDesktop) {
        d = this.buildDesktopPath(card, connector, isRight, dims)
      } else {
        const result = this.buildMobilePath(svg, card, connector, index, isRight, dims, borderWidth)
        d = result.d
        stroke = result.stroke
      }

      borderPath.setAttribute('d', d)
      setupDrawAnimation(borderPath, stroke, borderWidth)

      svg.appendChild(borderPath)
      card.appendChild(svg)
      card.style.borderColor = 'transparent'

      this.borderSVGs.push({ svg, path: borderPath })
    })
  }

  buildDesktopPath(card, connector, isRight, dims) {
    const {
      width, height, strokeOffset, cornerRadius,
    } = dims
    const connRect = connector.getBoundingClientRect()
    const cardRect = card.getBoundingClientRect()
    const stem = 20

    // stemX and dy are in the border SVG coordinate system.
    // The SVG origin aligns with the card's border-box origin.
    const lineEl = this.hasLineTarget ? this.lineTarget : null
    const lineCenter = lineEl
      ? lineEl.getBoundingClientRect().left + lineEl.offsetWidth / 2
      : connRect.left + (isRight ? 0 : 100)
    const stemX = lineCenter - cardRect.left
    const dy = connRect.top - cardRect.top
    const stemStartY = dy - stem

    // Cubic Bézier with vertical tangents at both endpoints for a smooth
    // stem-to-border transition (no kink at the junction).
    const edgeX = isRight ? strokeOffset : width - strokeOffset
    const horizDist = Math.abs(stemX - edgeX)
    let meetY = dy + Math.max(100, horizDist)
    meetY = Math.max(
      strokeOffset + cornerRadius,
      Math.min(meetY, height - strokeOffset - cornerRadius),
    )
    const cpOff = (meetY - dy) * 0.55

    const stemSegment = [`M ${stemX},${stemStartY}`, `L ${stemX},${dy}`]
    const bezier = [
      `C ${stemX},${dy + cpOff}`,
      `${edgeX},${meetY - cpOff}`,
      `${edgeX},${meetY}`,
    ].join(' ')
    const perimeter = buildBorderPerimeter(isRight, dims, meetY)

    return [...stemSegment, bezier, ...perimeter].join(' ')
  }

  buildMobilePath(svg, card, connector, index, isRight, dims, borderWidth) {
    const { width, strokeOffset, cornerRadius } = dims
    const connRect = connector.getBoundingClientRect()
    const cardRect = card.getBoundingClientRect()
    const dx = connRect.left - (cardRect.left - borderWidth)
    const dy = connRect.top - (cardRect.top - borderWidth)
    const size = 100
    const stem = 20

    // Arc center: right -> (dx, dy+size), left -> (dx+size, dy+size).
    // Find where the arc crosses the card's top border center.
    const sinTheta = Math.max(-1, Math.min(1, (strokeOffset - dy - size) / size))
    const cosTheta = Math.sqrt(1 - sinTheta * sinTheta)
    let meetX = isRight ? dx + size * cosTheta : dx + size - size * cosTheta
    meetX = Math.max(
      strokeOffset + cornerRadius,
      Math.min(meetX, width - strokeOffset - cornerRadius),
    )

    const stemX = isRight ? dx : dx + size
    const stemStartY = dy - stem

    // Gradient: connector portion transitions colors, border is solid.
    const currColor = connectorColor(connector)
    const prevColor = index > 0
      ? connectorColor(this.connectorTargets[index - 1])
      : null

    const gradId = `${this.uid}-grad-${index}`
    const defs = createSVGElement('defs')
    const grad = createSVGElement('linearGradient', {
      id: gradId,
      gradientUnits: 'userSpaceOnUse',
      x1: '0',
      y1: String(stemStartY),
      x2: '0',
      y2: String(strokeOffset),
    })

    if (index === 0) {
      grad.appendChild(createGradientStop('0%', currColor, 0))
      grad.appendChild(createGradientStop('80%', currColor))
    } else {
      grad.appendChild(createGradientStop('0%', prevColor))
      grad.appendChild(createGradientStop('100%', currColor))
    }
    defs.appendChild(grad)
    svg.appendChild(defs)

    const stemSegment = [`M ${stemX},${stemStartY}`, `L ${stemX},${dy}`]
    const arc = `A ${size},${size} 0 0 ${isRight ? '0' : '1'} ${meetX},${strokeOffset}`
    const perimeter = buildMobilePerimeter(isRight, dims, meetX)

    return {
      d: [...stemSegment, arc, ...perimeter].join(' '),
      stroke: `url(#${gradId})`,
    }
  }

  setInitialStates() {
    if (this.hasLineTarget && this.isDesktop) {
      this.lineTarget.style.clipPath = 'inset(0 0 100% 0)'
      this.lineTarget.style.transition = 'none'
    }

    const sectionEl = this.element.closest('section') || this.element.parentElement
    this.sectionBg = getComputedStyle(sectionEl).backgroundColor

    this.cardTargets.forEach((card) => {
      if (!card.dataset.originalBg) {
        card.dataset.originalBg = getComputedStyle(card).backgroundColor
      }
      if (!card.dataset.originalShadow) {
        card.dataset.originalShadow = getComputedStyle(card).boxShadow
      }
      card.style.backgroundColor = this.sectionBg
      card.style.boxShadow = 'none'
      card.style.transition = 'none'
    })

    this.cardContentTargets.forEach((content) => {
      content.style.clipPath = 'inset(0 0 100% 0)'
      content.style.transition = 'none'
    })
  }

  buildDesktopTimeline() {
    const {
      connectorDraw, borderDraw, contentReveal, pause,
    } = this.constructor.DESKTOP_TIMING
    const positions = this.cachedPositions
    if (positions.length === 0) return

    const firstPosition = positions[0] || 0
    const prePinTarget = firstPosition

    this.preTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: this.sectionEl,
        start: 'top bottom',
        end: 'top top',
        scrub: this.scrubValue,
      },
    })

    if (this.hasLineTarget && prePinTarget > 0) {
      const preClipBottom = 100 - (prePinTarget * 100)
      this.preTimeline.fromTo(
        this.lineTarget,
        { clipPath: 'inset(0 0 100% 0)' },
        { clipPath: `inset(0 0 ${preClipBottom}% 0)`, ease: 'none' },
      )
    }

    this.timeline = gsap.timeline({
      scrollTrigger: {
        trigger: this.sectionEl,
        start: this.startValue,
        end: `+=${this.naturalHeight}`,
        scrub: this.scrubValue,
        pin: true,
        anticipatePin: 1,
      },
    })

    const pausePerItem = connectorDraw + borderDraw + contentReveal + pause
    const totalPauseTime = pausePerItem * positions.length
    const totalLineTime = 1 - totalPauseTime
    const totalLineDistance = Math.max(1 - firstPosition, 0.001)
    const maxTranslate = Math.max(0, this.naturalHeight - this.viewportH)

    let currentTime = 0
    let prevPosition = firstPosition

    // Anchor the line at its pre-pin end state so the pinned timeline
    // doesn't reset it to the initial hidden clipPath.
    if (this.hasLineTarget) {
      const initialClipBottom = 100 - (firstPosition * 100)
      this.timeline.set(
        this.lineTarget,
        { clipPath: `inset(0 0 ${initialClipBottom}% 0)` },
        0,
      )
    }

    positions.forEach((position, index) => {
      const distance = position - prevPosition
      const lineDuration = (distance / totalLineDistance) * totalLineTime

      if (this.hasLineTarget && lineDuration > 0) {
        const clipBottom = 100 - (position * 100)
        this.timeline.to(
          this.lineTarget,
          { clipPath: `inset(0 0 ${clipBottom}% 0)`, duration: lineDuration, ease: 'none' },
          currentTime,
        )
      }

      const connectorPixelY = position * this.naturalHeight
      const translateY = Math.min(
        Math.max(0, connectorPixelY - this.viewportH * 0.35),
        maxTranslate,
      )
      if (lineDuration > 0) {
        this.timeline.to(
          this.innerWrap,
          { y: -translateY, duration: lineDuration, ease: 'none' },
          currentTime,
        )
      }

      currentTime += lineDuration

      // Combined connector + border draws as one path
      const borderSVG = this.borderSVGs[index]
      if (borderSVG) {
        const drawDuration = connectorDraw + borderDraw
        this.timeline.to(
          borderSVG.path,
          { strokeDashoffset: 0, duration: drawDuration, ease: 'none' },
          currentTime,
        )
      }
      currentTime += connectorDraw + borderDraw

      // Card background + content reveal
      if (this.cardTargets[index]) {
        const card = this.cardTargets[index]
        this.timeline.to(
          card,
          {
            backgroundColor: card.dataset.originalBg || 'rgb(255, 255, 255)',
            boxShadow: card.dataset.originalShadow || 'none',
            duration: contentReveal,
            ease: 'none',
          },
          currentTime,
        )
      }
      if (this.cardContentTargets[index]) {
        this.timeline.to(
          this.cardContentTargets[index],
          { clipPath: 'inset(0 0 0% 0)', duration: contentReveal, ease: 'none' },
          currentTime,
        )
      }
      currentTime += contentReveal + pause

      prevPosition = position
    })

    const remaining = 1 - prevPosition
    if (remaining > 0) {
      const remainingDuration = (remaining / totalLineDistance) * totalLineTime
      this.timeline.to(
        this.innerWrap,
        { y: -maxTranslate, duration: remainingDuration, ease: 'none' },
        currentTime,
      )
    }
  }

  // Mobile animation — no pin, per-item scroll triggers

  setupMobileAnimation() {
    this.cleanup()
    this.isDesktop = false

    this.activateConnectors()
    this.createBorderSVGs()
    this.setInitialStates()
    this.buildMobileTimelines()
  }

  buildMobileTimelines() {
    const { borderDrawFull, bgReveal, contentReveal } = this.constructor.MOBILE_TIMING
    this.mobileTimelines = []

    this.connectorTargets.forEach((connector, index) => {
      const item = connector.closest('.timeline-item')
      const borderSVG = this.borderSVGs[index]
      const card = this.cardTargets[index]
      const cardContent = this.cardContentTargets[index]

      // Each item gets its own scroll-triggered timeline.
      // Bottom-anchored end trigger ensures animation finishes
      // before the next item's trigger begins.
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: item,
          start: 'top 80%',
          end: 'bottom 75%',
          scrub: this.scrubValue,
        },
      })

      let t = 0

      if (borderSVG) {
        tl.to(
          borderSVG.path,
          { strokeDashoffset: 0, duration: borderDrawFull, ease: 'none' },
          t,
        )
      }
      t += borderDrawFull

      if (card) {
        tl.to(
          card,
          {
            backgroundColor: card.dataset.originalBg || 'rgb(255, 255, 255)',
            boxShadow: card.dataset.originalShadow || 'none',
            duration: bgReveal,
            ease: 'none',
          },
          t,
        )
      }
      if (cardContent) {
        tl.to(
          cardContent,
          { clipPath: 'inset(0 0 0% 0)', duration: contentReveal, ease: 'none' },
          t,
        )
      }

      this.mobileTimelines.push(tl)
    })
  }

  cleanup() {
    this._pinActive = false

    killTimeline(this.preTimeline)
    this.preTimeline = null

    killTimeline(this.timeline)
    this.timeline = null

    if (this.mobileTimelines) {
      this.mobileTimelines.forEach(killTimeline)
      this.mobileTimelines = []
    }

    this.borderSVGs.forEach(({ svg }) => svg.remove())
    this.borderSVGs = []

    if (this.innerWrap && this.innerWrap.parentNode === this.element) {
      gsap.set(this.innerWrap, { clearProps: 'y' })
      while (this.innerWrap.firstChild) {
        this.element.appendChild(this.innerWrap.firstChild)
      }
      this.innerWrap.remove()
      this.innerWrap = null
    }

    this.element.style.height = ''
    this.element.style.overflow = ''
    this.element.style.paddingTop = ''
    this.element.style.paddingBottom = ''

    if (this.hasLineTarget) {
      this.lineTarget.style.clipPath = ''
      this.lineTarget.style.transition = ''
      this.lineTarget.style.background = ''
    }

    this.connectorTargets.forEach((c) => c.classList.remove('tree-growing-active'))

    this.cardTargets.forEach((card) => {
      card.style.backgroundColor = ''
      card.style.borderColor = ''
      card.style.boxShadow = ''
      card.style.transition = ''
      delete card.dataset.originalBg
      delete card.dataset.originalShadow
    })

    this.cardContentTargets.forEach((content) => {
      content.style.clipPath = ''
      content.style.transition = ''
    })
  }
}
